<script setup lang="ts">
import { useMediaQuery, useWindowSize, watchDebounced } from '@vueuse/core';
import { ChevronDown, ChevronUp, GripVertical, Trash2 } from 'lucide-vue-next';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { computed, onUnmounted, ref, watch } from 'vue';
import { Spinner } from '@/components/ui/spinner';
import { useTranslations } from '@/composables/useTranslations';
import { loadPdfJs } from '@/lib/pdfjsClient';
import { cn } from '@/lib/utils';

const props = withDefaults(
    defineProps<{
        file: File;
        /** True while the parent POST / download is in progress. */
        submitting?: boolean;
        /** When false, hide the header “Download PDF” link (e.g. when the page has a primary button elsewhere). */
        showHeaderDownload?: boolean;
    }>(),
    { submitting: false, showHeaderDownload: true },
);

const pageOrder = defineModel<number[]>({ required: true });

const emit = defineEmits<{
    ready: [];
    error: [message: string];
    submit: [];
}>();

const { t } = useTranslations();
const { width: viewportWidth } = useWindowSize();
/** Native HTML5 drag is unreliable on touch; only enable for wide viewports with fine pointer (mouse / trackpad). */
const allowNativeCardDrag = useMediaQuery(
    '(min-width: 1024px) and (pointer: fine)',
);

const loading = ref(true);
const loadError = ref('');
const thumbnails = ref<Record<number, string>>({});
const dragIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

let pdfDoc: PDFDocumentProxy | null = null;
let loadGeneration = 0;

const outputCount = computed(() => pageOrder.value.length);

const gridHint = computed(() =>
    t(
        'tools.reorder_grid.hint',
        'Drag cards to change the order of pages in your new PDF.',
    ),
);

/** Raster width for thumbnails (smaller on narrow viewports = less memory & faster). */
function thumbParamsForViewport(cssWidth: number): {
    maxWidth: number;
    quality: number;
} {
    if (cssWidth < 360) {
        return { maxWidth: 72, quality: 0.7 };
    }

    if (cssWidth < 480) {
        return { maxWidth: 88, quality: 0.74 };
    }

    if (cssWidth < 640) {
        return { maxWidth: 104, quality: 0.78 };
    }

    if (cssWidth < 768) {
        return { maxWidth: 120, quality: 0.8 };
    }

    if (cssWidth < 1024) {
        return { maxWidth: 144, quality: 0.81 };
    }

    if (cssWidth < 1280) {
        return { maxWidth: 188, quality: 0.82 };
    }

    return { maxWidth: 216, quality: 0.83 };
}

function tr(
    key: string,
    fallback: string,
    replacements: Record<string, string>,
): string {
    let s = t(key, fallback);

    for (const [k, v] of Object.entries(replacements)) {
        s = s.replaceAll(`:${k}`, v);
    }

    return s;
}

async function renderThumbnail(
    doc: PDFDocumentProxy,
    pageNum: number,
    maxWidth: number,
    jpegQuality: number,
): Promise<string> {
    const page = await doc.getPage(pageNum);
    const base = page.getViewport({ scale: 1 });
    const scale = maxWidth / base.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return '';
    }

    await page.render({
        canvas,
        canvasContext: ctx,
        viewport,
        background: 'rgb(255,255,255)',
    }).promise;

    return canvas.toDataURL('image/jpeg', jpegQuality);
}

async function loadDocument(): Promise<void> {
    const gen = ++loadGeneration;
    loading.value = true;
    loadError.value = '';
    thumbnails.value = {};
    pdfDoc?.destroy();
    pdfDoc = null;

    try {
        const data = await props.file.arrayBuffer();

        if (gen !== loadGeneration) {
            return;
        }

        const { getDocument, VerbosityLevel } = await loadPdfJs();

        pdfDoc = await getDocument({
            data,
            verbosity: VerbosityLevel.ERRORS,
        }).promise;

        const n = pdfDoc.numPages;

        if (gen !== loadGeneration) {
            return;
        }

        if (pageOrder.value.length === 0) {
            pageOrder.value = Array.from({ length: n }, (_, i) => i + 1);
        } else {
            pageOrder.value = pageOrder.value.filter((p) => p >= 1 && p <= n);

            if (pageOrder.value.length === 0) {
                pageOrder.value = Array.from({ length: n }, (_, i) => i + 1);
            }
        }

        const thumbs: Record<number, string> = {};
        const w = viewportWidth.value > 32 ? viewportWidth.value : 1024;
        const { maxWidth: thumbMaxWidth, quality: thumbQuality } =
            thumbParamsForViewport(w);

        for (let p = 1; p <= n; p++) {
            if (gen !== loadGeneration || !pdfDoc) {
                return;
            }

            thumbs[p] = await renderThumbnail(
                pdfDoc,
                p,
                thumbMaxWidth,
                thumbQuality,
            );
        }

        if (gen !== loadGeneration) {
            return;
        }

        thumbnails.value = thumbs;
        emit('ready');
    } catch {
        if (gen === loadGeneration) {
            loadError.value = t(
                'tools.reorder_grid.load_error',
                'Could not load this PDF for preview.',
            );
            emit('error', loadError.value);
        }
    } finally {
        if (gen === loadGeneration) {
            loading.value = false;
        }
    }
}

watch(
    () => props.file,
    () => {
        void loadDocument();
    },
    { immediate: true },
);

const thumbLayoutBucket = computed(
    () =>
        thumbParamsForViewport(
            viewportWidth.value > 32 ? viewportWidth.value : 1024,
        ).maxWidth,
);

watchDebounced(
    thumbLayoutBucket,
    (next, prev) => {
        if (prev === undefined || next === prev || !props.file) {
            return;
        }

        void loadDocument();
    },
    { debounce: 450 },
);

function onDragStart(index: number): void {
    if (!allowNativeCardDrag.value) {
        return;
    }

    dragIndex.value = index;
}

function onDragEnd(): void {
    dragIndex.value = null;
    dragOverIndex.value = null;
}

function onDragEnter(index: number): void {
    if (dragIndex.value !== null) {
        dragOverIndex.value = index;
    }
}

function onDragLeave(index: number, event: DragEvent): void {
    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as HTMLElement;

    if (related && current.contains(related)) {
        return;
    }

    if (dragOverIndex.value === index) {
        dragOverIndex.value = null;
    }
}

function onDrop(targetIndex: number): void {
    if (dragIndex.value === null) {
        return;
    }

    const from = dragIndex.value;

    if (from === targetIndex) {
        dragIndex.value = null;
        dragOverIndex.value = null;

        return;
    }

    const next = [...pageOrder.value];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    pageOrder.value = next;
    dragIndex.value = null;
    dragOverIndex.value = null;
}

function removeAt(index: number): void {
    if (pageOrder.value.length <= 1) {
        return;
    }

    const next = [...pageOrder.value];
    next.splice(index, 1);
    pageOrder.value = next;
}

function movePage(fromIndex: number, delta: number): void {
    const toIndex = fromIndex + delta;

    if (toIndex < 0 || toIndex >= pageOrder.value.length) {
        return;
    }

    const next = [...pageOrder.value];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    pageOrder.value = next;
}

onUnmounted(() => {
    loadGeneration++;
    pdfDoc?.destroy();
    pdfDoc = null;
});
</script>

<template>
    <div class="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-5">
        <div
            class="mb-2 flex flex-col gap-3 sm:mb-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-2"
        >
            <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ t('tools.reorder_grid.title', 'Page order') }}
                </p>
                <p
                    class="mt-0.5 max-w-prose text-xs leading-snug text-gray-500 dark:text-gray-400"
                >
                    {{ gridHint }}
                </p>
            </div>
            <div
                v-if="
                    props.showHeaderDownload &&
                    !loading &&
                    !loadError &&
                    outputCount > 0
                "
                class="flex shrink-0 flex-col gap-1 text-left sm:items-end sm:text-right"
            >
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{
                        tr(
                            'tools.reorder_grid.output_count',
                            ':count page(s) in exported PDF',
                            {
                                count: String(outputCount),
                            },
                        )
                    }}
                </p>
                <button
                    type="button"
                    class="text-sm font-semibold text-brand-600 underline-offset-2 hover:text-brand-700 hover:underline disabled:pointer-events-none disabled:opacity-50 dark:text-brand-400 dark:hover:text-brand-300"
                    :disabled="props.submitting"
                    @click="emit('submit')"
                >
                    {{ t('tools.reorder_grid.download', 'Download PDF') }}
                </button>
            </div>
        </div>

        <div
            v-if="loading"
            class="flex min-h-[8.5rem] items-center justify-center rounded-lg border border-dashed border-border bg-muted/50 sm:min-h-[10rem] dark:bg-muted/30"
            aria-busy="true"
        >
            <Spinner
                class="size-7 text-gray-400 sm:size-8 dark:text-gray-500"
            />
        </div>

        <div
            v-else-if="loadError"
            class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
        >
            {{ loadError }}
        </div>

        <ul
            v-else
            class="grid max-h-[min(64vh,50rem)] grid-cols-2 gap-2 overflow-y-auto overscroll-y-contain pr-0.5 min-[400px]:grid-cols-3 min-[400px]:gap-3 sm:max-h-[min(70vh,54rem)] md:max-h-[min(72vh,56rem)] md:grid-cols-4 lg:max-h-[min(76vh,60rem)] lg:grid-cols-5"
            role="list"
        >
            <li
                v-for="(originalPage, outputIndex) in pageOrder"
                :key="`${outputIndex}-${originalPage}`"
                class="relative touch-manipulation [-webkit-tap-highlight-color:transparent]"
                role="listitem"
                :draggable="allowNativeCardDrag"
                @dragstart="onDragStart(outputIndex)"
                @dragend="onDragEnd"
                @dragenter.prevent="onDragEnter(outputIndex)"
                @dragleave="onDragLeave(outputIndex, $event)"
                @dragover.prevent
                @drop.prevent="onDrop(outputIndex)"
            >
                <div
                    :class="
                        cn(
                            'group flex h-full flex-col overflow-hidden rounded-md border bg-white shadow-sm transition sm:rounded-lg dark:bg-gray-950',
                            dragIndex === outputIndex &&
                                'scale-[0.98] opacity-60 ring-2 ring-brand-500',
                            dragOverIndex === outputIndex &&
                                dragIndex !== null &&
                                dragIndex !== outputIndex &&
                                'ring-2 ring-brand-400 ring-offset-2 dark:ring-offset-gray-900',
                            'border-border',
                        )
                    "
                >
                    <div
                        class="relative aspect-[4/5] w-full shrink-0 bg-gray-100 sm:aspect-[3/4] dark:bg-gray-900"
                    >
                        <div
                            v-show="allowNativeCardDrag"
                            class="pointer-events-none absolute top-1 left-1 z-10 flex items-center rounded bg-white/95 px-0.5 py-0.5 shadow-sm sm:top-1.5 sm:left-1.5 sm:rounded-md sm:px-1 dark:bg-gray-900/95"
                            aria-hidden="true"
                        >
                            <GripVertical
                                class="size-3 shrink-0 text-gray-400 sm:size-3.5"
                            />
                        </div>

                        <button
                            type="button"
                            class="absolute top-1 right-1 z-20 flex size-9 min-h-9 min-w-9 items-center justify-center rounded-full bg-red-600 text-white shadow-md transition hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:outline-none active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 sm:top-1.5 sm:right-1.5 sm:size-8 sm:min-h-0 sm:min-w-0 sm:opacity-0 sm:group-hover:opacity-100 dark:bg-red-700 dark:hover:bg-red-600"
                            :disabled="pageOrder.length <= 1"
                            :aria-label="
                                t(
                                    'tools.reorder_grid.remove_aria',
                                    'Remove page from export',
                                )
                            "
                            @click.stop="removeAt(outputIndex)"
                            @mousedown.stop
                        >
                            <Trash2 class="size-3.5" stroke-width="2.5" />
                        </button>

                        <img
                            v-if="thumbnails[originalPage]"
                            :src="thumbnails[originalPage]"
                            alt=""
                            class="h-full w-full object-contain object-top"
                        />
                        <div
                            v-else
                            class="flex h-full items-center justify-center text-xs text-gray-400"
                        >
                            {{ t('tools.reorder_grid.thumb_pending', '…') }}
                        </div>
                    </div>

                    <div
                        class="border-t border-gray-100 bg-gray-50/90 px-1.5 py-1.5 text-center sm:px-2 sm:py-2 dark:border-gray-800 dark:bg-gray-900/80"
                    >
                        <p
                            class="line-clamp-2 text-[10px] leading-tight font-semibold text-gray-900 min-[420px]:text-[11px] sm:text-xs dark:text-gray-100"
                        >
                            {{
                                tr(
                                    'tools.reorder_grid.new_page',
                                    'Will be page :n in new PDF',
                                    {
                                        n: String(outputIndex + 1),
                                    },
                                )
                            }}
                        </p>
                        <p
                            class="mt-0.5 text-[10px] leading-tight text-gray-500 min-[420px]:text-[11px] dark:text-gray-400"
                        >
                            {{
                                tr(
                                    'tools.reorder_grid.original_page',
                                    'Original PDF page :n',
                                    {
                                        n: String(originalPage),
                                    },
                                )
                            }}
                        </p>

                        <div
                            v-show="!allowNativeCardDrag"
                            class="mt-2 flex justify-center gap-1.5 border-t border-gray-100 pt-2 dark:border-gray-800"
                            role="group"
                            :aria-label="
                                t(
                                    'tools.reorder_grid.reorder_controls_aria',
                                    'Reorder this page',
                                )
                            "
                        >
                            <button
                                type="button"
                                class="flex size-10 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 shadow-sm active:bg-gray-50 disabled:pointer-events-none disabled:opacity-35 dark:border-border dark:bg-card dark:text-foreground dark:active:bg-muted"
                                :disabled="outputIndex === 0"
                                :aria-label="
                                    t(
                                        'tools.reorder_grid.move_up_aria',
                                        'Move page earlier in the list',
                                    )
                                "
                                @click.stop="movePage(outputIndex, -1)"
                            >
                                <ChevronUp
                                    class="size-5 shrink-0"
                                    stroke-width="2.25"
                                />
                            </button>
                            <button
                                type="button"
                                class="flex size-10 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 shadow-sm active:bg-gray-50 disabled:pointer-events-none disabled:opacity-35 dark:border-border dark:bg-card dark:text-foreground dark:active:bg-muted"
                                :disabled="outputIndex >= pageOrder.length - 1"
                                :aria-label="
                                    t(
                                        'tools.reorder_grid.move_down_aria',
                                        'Move page later in the list',
                                    )
                                "
                                @click.stop="movePage(outputIndex, 1)"
                            >
                                <ChevronDown
                                    class="size-5 shrink-0"
                                    stroke-width="2.25"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>
